import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import LocalizedLink from './LocalizedLink';
import { ArrowRight, ArrowLeftRight, Flame } from 'lucide-react';
import SEOHead from './SEOHead';
import { fuelEfficiencyMassUnits, popularFuelEfficiencyMassConversions, getUnitById, convertFuelEfficiencyMass } from '../data/fuelEfficiencyMassRoutes';

// Format numbers for display
const formatNumber = (num) => {
  if (num === 0) return '0';
  if (Math.abs(num) < 0.000001 || Math.abs(num) > 1000000000) {
    return num.toExponential(6);
  }
  return parseFloat(num.toPrecision(10)).toString();
};

const FuelEfficiencyMassConverterIndex = () => {
  const { t } = useTranslation();
  const [fromUnit, setFromUnit] = useState('joule-per-kilogram');
  const [toUnit, setToUnit] = useState('kilojoule-per-kilogram');
  const [fromValue, setFromValue] = useState('1');
  const [toValue, setToValue] = useState('');

  useEffect(() => {
    if (fromUnit && toUnit && fromValue) {
      const numValue = parseFloat(fromValue);
      if (!isNaN(numValue)) {
        const result = convertFuelEfficiencyMass(numValue, fromUnit, toUnit);
        if (result !== null) {
          setToValue(formatNumber(result));
        }
      }
    }
  }, [fromValue, fromUnit, toUnit]);

  const handleSwap = () => {
    setFromUnit(toUnit);
    setToUnit(fromUnit);
    setFromValue(toValue);
  };

  const fromUnitData = getUnitById(fromUnit);
  const toUnitData = getUnitById(toUnit);

  return (
    <div className="space-y-6">
      <SEOHead
        title={t('converters.fuelEfficiencyMass.name')}
        description={t('converters.fuelEfficiencyMass.seoDescription')}
      />

      {/* Header with Description */}
      <div className="bg-white rounded-lg shadow-sm border border-gray-200 overflow-hidden">
        <div className="bg-gradient-to-r from-[#4a7c9b] to-[#5a8caa] px-4 py-3">
          <div className="flex items-center gap-2">
            <Flame className="h-5 w-5 text-white" />
            <h1 className="text-lg font-semibold text-white">{t('converters.fuelEfficiencyMass.title', t('converters.fuelEfficiencyMass.name'))}</h1>
          </div>
        </div>
        <div className="p-4">
          <p className="text-gray-600 text-sm">
            {t('converters.fuelEfficiencyMass.description')}
          </p>
        </div>
      </div>

      {/* Main Converter Tool */}
      <div className="bg-white rounded-lg shadow-sm border border-gray-200 overflow-hidden">
        <div className="p-6">
          <div className="grid md:grid-cols-[1fr,auto,1fr] gap-6 items-end">
            {/* From Section */}
            <div className="space-y-3">
              <label className="block text-sm font-medium text-gray-700">{t('common.from')}</label>
              <input
                type="number"
                value={fromValue}
                onChange={(e) => setFromValue(e.target.value)}
                className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-[#4a7c9b] focus:border-transparent text-lg"
                placeholder={t('common.enterValue')}
              />
              <select
                value={fromUnit}
                onChange={(e) => setFromUnit(e.target.value)}
                className="w-full px-4 py-3 border border-gray-300 rounded-lg bg-white focus:outline-none focus:ring-2 focus:ring-[#4a7c9b] focus:border-transparent"
              >
                {fuelEfficiencyMassUnits.map((unit) => (
                  <option key={unit.id} value={unit.id}>
                    {t(`fuelEfficiencyMassUnits.${unit.id}.name`, unit.name)} ({unit.symbol})
                  </option>
                ))}
              </select>
            </div>

            {/* Swap Button */}
            <div className="flex justify-center">
              <button
                onClick={handleSwap}
                className="p-3 rounded-full bg-[#4a7c9b] text-white hover:bg-[#3d6a86] transition-colors shadow-md"
                title={t('common.swap')}
              >
                <ArrowLeftRight size={24} />
              </button>
            </div>

            {/* To Section */}
            <div className="space-y-3">
              <label className="block text-sm font-medium text-gray-700">{t('common.to')}</label>
              <input
                type="text"
                value={toValue}
                readOnly
                className="w-full px-4 py-3 border border-gray-300 rounded-lg bg-gray-50 text-gray-700 text-lg"
                placeholder={t('common.result')}
              />
              <select
                value={toUnit}
                onChange={(e) => setToUnit(e.target.value)}
                className="w-full px-4 py-3 border border-gray-300 rounded-lg bg-white focus:outline-none focus:ring-2 focus:ring-[#4a7c9b] focus:border-transparent"
              >
                {fuelEfficiencyMassUnits.map((unit) => (
                  <option key={unit.id} value={unit.id}>
                    {t(`fuelEfficiencyMassUnits.${unit.id}.name`, unit.name)} ({unit.symbol})
                  </option>
                ))}
              </select>
            </div>
          </div>

          {/* Result Display */}
          {fromValue && toValue && fromUnitData && toUnitData && (
            <div className="mt-6 p-4 bg-blue-50 rounded-lg border border-blue-100">
              <p className="text-center text-lg text-gray-700">
                <span className="font-semibold">{fromValue}</span>{' '}
                <span className="text-gray-500">{t(`fuelEfficiencyMassUnits.${fromUnitData.id}.plural`, fromUnitData.plural)}</span> ={' '}
                <span className="font-semibold text-[#4a7c9b] text-xl">{toValue}</span>{' '}
                <span className="text-gray-500">{t(`fuelEfficiencyMassUnits.${toUnitData.id}.plural`, toUnitData.plural)}</span>
              </p>
            </div>
          )}
        </div>

        {/* Quick Reference Table */}
        <div className="border-t border-gray-200 p-4">
          <h3 className="text-sm font-semibold text-gray-700 mb-3">{t('common.quickReference')}</h3>
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="bg-gray-50">
                  <th className="px-3 py-2 text-left font-medium text-gray-600">
                    {t(`fuelEfficiencyMassUnits.${fromUnitData?.id}.plural`, fromUnitData?.plural)} ({fromUnitData?.symbol})
                  </th>
                  <th className="px-3 py-2 text-left font-medium text-gray-600">
                    {t(`fuelEfficiencyMassUnits.${toUnitData?.id}.plural`, toUnitData?.plural)} ({toUnitData?.symbol})
                  </th>
                </tr>
              </thead>
              <tbody>
                {[1, 5, 10, 25, 50, 100].map((val) => {
                  const result = convertFuelEfficiencyMass(val, fromUnit, toUnit);
                  return (
                    <tr key={val} className="border-t border-gray-100">
                      <td className="px-3 py-2">{val} {fromUnitData?.symbol}</td>
                      <td className="px-3 py-2 text-[#4a7c9b] font-medium">
                        {formatNumber(result)} {toUnitData?.symbol}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        </div>
      </div>

      {/* Popular Fuel Efficiency Mass Unit Conversions */}
      <div className="bg-white rounded-lg shadow-sm border border-gray-200 overflow-hidden">
        <div className="px-4 py-3 border-b border-gray-200 bg-gray-50">
          <h2 className="font-semibold text-gray-800">{t('converters.fuelEfficiencyMass.popularTitle')}</h2>
        </div>
        <div className="p-4">
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-2">
            {popularFuelEfficiencyMassConversions.map((conversion, index) => {
              const fromU = getUnitById(conversion.from);
              const toU = getUnitById(conversion.to);
              if (!fromU || !toU) return null;

              return (
                <LocalizedLink
                  key={index}
                  to={`/convert/fuel-efficiency-mass/${conversion.from}-to-${conversion.to}`}
                  className="flex items-center justify-between px-3 py-2 rounded-lg hover:bg-blue-50 text-gray-700 hover:text-[#2563eb] transition-colors group"
                >
                  <span className="text-sm">
                    {t('common.xToY', { from: t(`fuelEfficiencyMassUnits.${fromU.id}.plural`, fromU.plural), to: t(`fuelEfficiencyMassUnits.${toU.id}.plural`, toU.plural) })}
                  </span>
                  <ArrowRight className="h-4 w-4 text-gray-400 group-hover:text-[#2563eb] transition-colors" />
                </LocalizedLink>
              );
            })}
          </div>
        </div>
      </div>

      {/* Complete List of Fuel Efficiency Mass Units */}
      <div className="bg-white rounded-lg shadow-sm border border-gray-200 overflow-hidden">
        <div className="px-4 py-3 border-b border-gray-200 bg-gray-50">
          <h2 className="font-semibold text-gray-800">{t('common.completeList', { category: t('converters.fuelEfficiencyMass.name') })}</h2>
        </div>
        <div className="p-4">
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
            {fuelEfficiencyMassUnits.map((unit) => (
              <div key={unit.id} className="border border-gray-200 rounded-lg p-3">
                <h3 className="font-medium text-gray-800 mb-2">
                  {t(`fuelEfficiencyMassUnits.${unit.id}.name`, unit.name)} ({unit.symbol})
                </h3>
                <div className="space-y-1">
                  {fuelEfficiencyMassUnits
                    .filter((u) => u.id !== unit.id)
                    .slice(0, 5)
                    .map((targetUnit) => (
                      <LocalizedLink
                        key={targetUnit.id}
                        to={`/convert/fuel-efficiency-mass/${unit.id}-to-${targetUnit.id}`}
                        className="block text-sm text-gray-600 hover:text-[#2563eb] transition-colors"
                      >
                        {t('common.xToY', { from: t(`fuelEfficiencyMassUnits.${unit.id}.name`, unit.name), to: t(`fuelEfficiencyMassUnits.${targetUnit.id}.name`, targetUnit.name) })}
                      </LocalizedLink>
                    ))}
                  <LocalizedLink
                    to={`/convert/fuel-efficiency-mass/${unit.id}-to-${fuelEfficiencyMassUnits.find(u => u.id !== unit.id)?.id}`}
                    className="block text-sm text-[#2563eb] hover:underline mt-1"
                  >
                    {t('common.moreConversions', { unit: t(`fuelEfficiencyMassUnits.${unit.id}.name`, unit.name) })} →
                  </LocalizedLink>
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>

      {/* Back to Home Link */}
      <div className="text-center">
        <LocalizedLink
          to="/"
          className="text-[#2563eb] hover:underline text-sm"
        >
          ← {t('common.backToAllConverters')}
        </LocalizedLink>
      </div>
    </div>
  );
};

export default FuelEfficiencyMassConverterIndex;
